import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Users, Search, Loader2, ShieldCheck, DollarSign, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ImpactRegionsAdmin } from "@/components/sections/ImpactRegionsAdmin";

const money = (cents, currency = "usd") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format((cents || 0) / 100);

export function AdminWaitlist() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/subscribers")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load subscribers."))
      .finally(() => setLoading(false));
  }, []);

  const exportCsv = () => {
    if (!data?.subscribers?.length) return;
    const rows = [["Email", "Cohort", "Plan", "Amount", "Joined"]];
    data.subscribers.forEach((s) => rows.push([
      s.email, s.cohort || "", s.interval === "year" ? "Annual" : "Monthly",
      money(s.amount, s.currency), s.created_at ? new Date(s.created_at).toISOString() : "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `occ-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Subscribers exported as CSV.");
  };

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <ShieldCheck className="h-10 w-10 text-slate-300 mx-auto mb-4" />
        <h2 className="font-serif text-3xl font-semibold text-navy">Admin access required</h2>
        <p className="text-slate-600 mt-3">This screen is reserved for OCC administrators.</p>
      </div>
    );
  }

  const filtered = (data?.subscribers || []).filter(
    (s) => s.email.toLowerCase().includes(q.toLowerCase()) || (s.cohort || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">Admin</span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-navy mt-2">Paid Subscribers</h1>
          <p className="text-slate-600 mt-2">Active OCC Premium members, revenue and cohort mix.</p>
        </div>
        <Button onClick={exportCsv} disabled={!data?.subscribers?.length} data-testid="subscribers-export-button"
          className="bg-persian-rose hover:bg-persian-rosehover text-white font-semibold">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5">
          <Users className="h-5 w-5 text-persian-blue" />
          <div className="font-serif text-3xl font-bold text-navy mt-2" data-testid="subscribers-total">{data?.total ?? "—"}</div>
          <div className="text-xs text-slate-500">Active subscribers</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5">
          <TrendingUp className="h-5 w-5 text-persian-blue" />
          <div className="font-serif text-3xl font-bold text-navy mt-2" data-testid="subscribers-mrr">
            {data ? money(data.mrr_cents) : "—"}
          </div>
          <div className="text-xs text-slate-500">Est. monthly recurring</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5">
          <DollarSign className="h-5 w-5 text-persian-rose" />
          <div className="font-serif text-3xl font-bold text-navy mt-2" data-testid="subscribers-revenue">
            {data ? money(data.revenue_cents) : "—"}
          </div>
          <div className="text-xs text-slate-500">Collected to date</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5">
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Cohorts</div>
          <div className="space-y-0.5">
            {data && Object.entries(data.cohorts || {}).slice(0, 3).map(([c, n]) => (
              <div key={c} className="flex justify-between text-sm text-navy">
                <span className="truncate mr-2">{c}</span><span className="font-mono">{n}</span>
              </div>
            ))}
            {data && Object.keys(data.cohorts || {}).length === 0 && <span className="text-sm text-slate-400">—</span>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-7 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="subscribers-search-input"
            placeholder="Search by email or cohort…" className="pl-9" />
        </div>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-persian-blue" /></div>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-slate-500 text-sm" data-testid="subscribers-empty">No paid subscribers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="subscribers-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Cohort</th>
                  <th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium">Amount</th>
                  <th className="px-5 py-3 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} data-testid={`subscriber-row-${s.id}`}
                    className="border-b border-slate-50 hover:bg-navy-soft/50 transition-colors">
                    <td className="px-5 py-3 text-navy font-medium">{s.email}</td>
                    <td className="px-5 py-3 text-slate-600">{s.cohort}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs rounded-full px-2 py-0.5 ${s.interval === "year" ? "bg-persian-rose/10 text-persian-rose" : "bg-persian-blue/10 text-navy"}`}>
                        {s.interval === "year" ? "Annual" : "Monthly"}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-navy">{money(s.amount, s.currency)}</td>
                    <td className="px-5 py-3 text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ImpactRegionsAdmin />
    </div>
  );
}
